// Fees — per-merchant fee scale editor. SUPERADMIN ONLY.
//
// Route: /fees, scoped to the merchant in the header switcher (the same
// X-Merchant-ID context every other id-less screen uses).
//
// The backend gate (auth.RequireSuperadmin) refuses plain admins, unlike
// every other admin route in this app: what a tenant is charged is the
// platform owner's call. The nav entry and this page mirror that, but the
// server is the authority — hiding a screen is not access control.
//
// Editing model: a scale is replaced WHOLE, per kind. Bands have to tile the
// range the way you meant them to, so there is no per-band save that could
// leave a merchant billed from half an edit. "Save scale" sends the lot.
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { useAuth, isSuperadminRole } from "@/store/auth";
import { useUI } from "@/store/ui";
import { useMerchant } from "@/queries/useMerchant";
import { apiErrorMessage } from "@/utils/apiError";
import { fmtCcyMinor } from "@/utils/format";
import {
  listFeeRules,
  replaceFeeRules,
  previewCharge,
  FEE_KINDS,
  type FeeKind,
  type FeeRule,
  type FeeRuleInput,
} from "@/api/merchantFees";

// What each kind charges against, in the operator's words. The basis is the
// single thing most likely to be misread — "2%" of WHAT — so it's stated on
// every scale rather than left to a tooltip.
const KIND_COPY: Record<
  FeeKind,
  { title: string; basis: string; blurb: string; unit: "money" | "count" }
> = {
  topup: {
    title: "Top-up",
    basis: "the amount credited",
    blurb:
      "Charged each time you add funds to this merchant's ledger. Opening balances and corrections are not charged — those are your bookkeeping, not their transaction.",
    unit: "money",
  },
  transaction: {
    title: "Per payout",
    basis: "the payout amount",
    blurb:
      "Charged when a payout succeeds. Failed payouts are never charged — the money didn't move.",
    unit: "money",
  },
  beneficiary: {
    title: "Per beneficiary, monthly",
    basis: "the number of beneficiaries paid",
    blurb:
      "Charged once a month for each distinct beneficiary this merchant actually paid. Someone paid forty times in a month counts once. Bands are headcounts, and the amount is per beneficiary — a percentage of a headcount means nothing, so that column is off here.",
    unit: "count",
  },
};

// ── paise <-> rupees at the input boundary ──────────────────────────────────
//
// Operators think in rupees; the wire is paise (CLAUDE.md rule 6). Convert in
// exactly these two places. Math.round on the way in because 12.34 * 100 is
// 1233.9999999999998 in IEEE 754 — truncating would quietly bill a paisa
// less on every band anyone typed a decimal into.
function rupeesToMinor(s: string): number {
  const n = Number.parseFloat(s);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}
function minorToRupees(m: number | null): string {
  if (m == null) return "";
  return (m / 100).toFixed(2);
}
// percent <-> basis points. 2.5% -> 250.
function percentToBp(s: string): number {
  const n = Number.parseFloat(s);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}
function bpToPercent(bp: number): string {
  return (bp / 100).toString();
}

/** A band while it's being edited: strings, because inputs hold strings and
 *  "" must stay distinguishable from 0 until the operator commits. */
interface DraftBand {
  from: string; // rupees, or a count for beneficiary
  to: string; // "" = and up
  flat: string; // rupees
  percent: string; // percent, e.g. "2.5"
  min: string; // rupees, "" = no floor
  max: string; // rupees, "" = no cap
}

const EMPTY_BAND: DraftBand = {
  from: "",
  to: "",
  flat: "",
  percent: "",
  min: "",
  max: "",
};

function ruleToDraft(r: FeeRule, unit: "money" | "count"): DraftBand {
  const asBasis = (v: number | null) =>
    v == null ? "" : unit === "count" ? String(v) : minorToRupees(v);
  return {
    from: asBasis(r.basis_from_minor),
    to: asBasis(r.basis_to_minor),
    flat: r.flat_minor ? minorToRupees(r.flat_minor) : "",
    percent: r.percent_bp ? bpToPercent(r.percent_bp) : "",
    min: minorToRupees(r.min_fee_minor),
    max: minorToRupees(r.max_fee_minor),
  };
}

function draftToInput(d: DraftBand, unit: "money" | "count"): FeeRuleInput {
  const basis = (s: string) =>
    unit === "count" ? Math.max(0, Math.round(Number(s) || 0)) : rupeesToMinor(s);
  return {
    basis_from_minor: basis(d.from),
    basis_to_minor: d.to.trim() === "" ? null : basis(d.to),
    flat_minor: d.flat.trim() === "" ? 0 : rupeesToMinor(d.flat),
    percent_bp: d.percent.trim() === "" ? 0 : percentToBp(d.percent),
    min_fee_minor: d.min.trim() === "" ? null : rupeesToMinor(d.min),
    max_fee_minor: d.max.trim() === "" ? null : rupeesToMinor(d.max),
  };
}

export function Fees() {
  const isSuperadmin = useAuth((s) => isSuperadminRole(s.user?.role));
  const currentMerchantId = useUI((s) => s.currentMerchantId);
  const { data: merchant } = useMerchant(currentMerchantId ?? undefined);

  const feesQuery = useQuery({
    queryKey: ["merchant-fees", currentMerchantId],
    queryFn: () => listFeeRules(currentMerchantId!),
    enabled: isSuperadmin && currentMerchantId != null,
    staleTime: 60_000,
  });

  if (!isSuperadmin) {
    return (
      <div className="page">
        <h1>Fees</h1>
        <p className="dim" style={{ maxWidth: 560 }}>
          Fee scales are managed by platform superadmins. If you need a
          merchant's pricing changed, ask one — an admin account can't set it,
          by design.
        </p>
      </div>
    );
  }

  if (currentMerchantId == null) {
    return (
      <div className="page">
        <h1>Fees</h1>
        <p className="dim">
          Pick a merchant in the switcher at the top of the page to see its fee
          scale.
        </p>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-head" style={{ marginBottom: 8 }}>
        <div className="row" style={{ alignItems: "baseline", gap: 12 }}>
          <h1 style={{ margin: 0 }}>Fees</h1>
          <span className="dim" style={{ fontSize: 13 }}>
            {merchant?.display_name ?? "this merchant"}
            {merchant?.slug && (
              <span className="mono" style={{ marginLeft: 6 }}>
                ({merchant.slug})
              </span>
            )}
          </span>
        </div>
        <p className="dim" style={{ fontSize: 12, marginTop: 6, maxWidth: 680 }}>
          Every charge lands in this merchant's ledger as a <code>fee</code>{" "}
          entry, so they can see what they were billed and why. A scale with no
          bands charges nothing — that's how you turn a fee off.
        </p>
      </div>

      {feesQuery.isLoading ? (
        <div className="empty">
          <div className="dim" style={{ fontSize: 13 }}>
            Loading fee scales…
          </div>
        </div>
      ) : feesQuery.isError ? (
        <div className="card" style={{ padding: 16 }}>
          <div style={{ color: "var(--warn, #b42318)", fontSize: 13 }}>
            {apiErrorMessage(
              feesQuery.error,
              "Could not load fee scales for this merchant.",
            )}
          </div>
        </div>
      ) : (
        FEE_KINDS.map((kind) => (
          <ScaleCard
            key={kind}
            kind={kind}
            merchantId={currentMerchantId}
            rules={(feesQuery.data ?? []).filter((r) => r.kind === kind)}
          />
        ))
      )}
    </div>
  );
}

function ScaleCard({
  kind,
  merchantId,
  rules,
}: {
  kind: FeeKind;
  merchantId: number;
  rules: FeeRule[];
}) {
  const copy = KIND_COPY[kind];
  const qc = useQueryClient();
  const addToast = useUI((s) => s.addToast);

  const saved = useMemo(
    () =>
      [...rules]
        .sort((a, b) => a.basis_from_minor - b.basis_from_minor)
        .map((r) => ruleToDraft(r, copy.unit)),
    [rules, copy.unit],
  );
  const [bands, setBands] = useState<DraftBand[]>(saved);
  const [error, setError] = useState<string | null>(null);

  // Re-seed when the server's copy changes (first load, or another tab saved).
  useEffect(() => {
    setBands(saved);
    setError(null);
  }, [saved]);

  const dirty = JSON.stringify(bands) !== JSON.stringify(saved);

  const save = useMutation({
    mutationFn: () =>
      replaceFeeRules(
        merchantId,
        kind,
        bands.map((b) => draftToInput(b, copy.unit)),
      ),
    onSuccess: () => {
      setError(null);
      addToast({ title: `${copy.title} fees saved.`, variant: "success" });
      void qc.invalidateQueries({ queryKey: ["merchant-fees", merchantId] });
    },
    onError: (err) =>
      setError(apiErrorMessage(err, "Could not save this scale.")),
  });

  function patch(i: number, field: keyof DraftBand, value: string) {
    setBands((prev) =>
      prev.map((b, idx) => (idx === i ? { ...b, [field]: value } : b)),
    );
  }

  // Local mirror of the server's arithmetic, so the operator sees the actual
  // charge before committing a scale rather than after billing someone.
  const inputs = bands.map((b) => draftToInput(b, copy.unit));
  const samples = sampleBases(inputs, copy.unit);

  return (
    <section className="card" style={{ marginTop: "var(--card-gap)" }}>
      <div className="card-head">
        <h3>{copy.title}</h3>
        <div className="spacer" />
        <span className="dim" style={{ fontSize: 11 }}>
          {bands.length === 0
            ? "not charged"
            : `${bands.length} band${bands.length === 1 ? "" : "s"}`}
        </span>
      </div>

      <div style={{ padding: "12px 16px" }}>
        <p className="dim" style={{ fontSize: 12, marginBottom: 12, maxWidth: 680 }}>
          {copy.blurb}
        </p>

        {bands.length === 0 ? (
          <div className="dim" style={{ fontSize: 12, marginBottom: 12 }}>
            No bands — this merchant is not charged a {copy.title.toLowerCase()}{" "}
            fee.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>
                    From
                    <span className="dim" style={{ fontWeight: 400 }}>
                      {" "}
                      ({copy.unit === "count" ? "beneficiaries" : "₹"})
                    </span>
                  </th>
                  <th>
                    To
                    <span className="dim" style={{ fontWeight: 400 }}>
                      {" "}
                      (blank = and up)
                    </span>
                  </th>
                  <th>Flat ₹</th>
                  {copy.unit === "money" && <th>Percent %</th>}
                  <th>Min ₹</th>
                  <th>Max ₹</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {bands.map((b, i) => (
                  <tr key={i}>
                    <td>
                      <NumCell
                        value={b.from}
                        onChange={(v) => patch(i, "from", v)}
                        label={`Band ${i + 1} from`}
                      />
                    </td>
                    <td>
                      <NumCell
                        value={b.to}
                        onChange={(v) => patch(i, "to", v)}
                        placeholder="∞"
                        label={`Band ${i + 1} to`}
                      />
                    </td>
                    <td>
                      <NumCell
                        value={b.flat}
                        onChange={(v) => patch(i, "flat", v)}
                        placeholder="0"
                        label={`Band ${i + 1} flat`}
                      />
                    </td>
                    {copy.unit === "money" && (
                      <td>
                        <NumCell
                          value={b.percent}
                          onChange={(v) => patch(i, "percent", v)}
                          placeholder="0"
                          label={`Band ${i + 1} percent`}
                        />
                      </td>
                    )}
                    <td>
                      <NumCell
                        value={b.min}
                        onChange={(v) => patch(i, "min", v)}
                        placeholder="—"
                        label={`Band ${i + 1} minimum`}
                      />
                    </td>
                    <td>
                      <NumCell
                        value={b.max}
                        onChange={(v) => patch(i, "max", v)}
                        placeholder="—"
                        label={`Band ${i + 1} maximum`}
                      />
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <button
                        className="icon-btn"
                        type="button"
                        title="Remove this band"
                        aria-label={`Remove band ${i + 1}`}
                        onClick={() =>
                          setBands((prev) => prev.filter((_, idx) => idx !== i))
                        }
                      >
                        <I.Trash size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {samples.length > 0 && (
          <div
            style={{
              marginTop: 12,
              padding: "10px 12px",
              border: "1px solid var(--hair)",
              borderRadius: 3,
              background: "var(--bg-elev)",
            }}
          >
            <div className="eyebrow" style={{ marginBottom: 6 }}>
              What this charges
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 18px" }}>
              {samples.map((s) => (
                <span key={s.basis} className="mono" style={{ fontSize: 12 }}>
                  <span className="dim">
                    {copy.unit === "count"
                      ? `${s.basis} bene`
                      : fmtCcyMinor(BigInt(s.basis), "INR")}
                  </span>
                  {" → "}
                  {s.charge == null ? (
                    <span className="dim">not charged</span>
                  ) : (
                    fmtCcyMinor(BigInt(s.charge), "INR")
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div
            style={{ color: "var(--warn, #b42318)", fontSize: 12, marginTop: 10 }}
          >
            {error}
          </div>
        )}

        <div className="row" style={{ gap: 8, marginTop: 14 }}>
          <button
            className="btn sm"
            type="button"
            onClick={() => setBands((prev) => [...prev, { ...EMPTY_BAND }])}
          >
            <I.Plus size={12} /> Add band
          </button>
          <div className="flex-1" />
          {dirty && (
            <button
              className="btn ghost sm"
              type="button"
              onClick={() => setBands(saved)}
              disabled={save.isPending}
            >
              Discard
            </button>
          )}
          <button
            className="btn accent sm"
            type="button"
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
          >
            {save.isPending ? "Saving…" : "Save scale"}
          </button>
        </div>
      </div>
    </section>
  );
}

function NumCell({
  value,
  onChange,
  placeholder,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  label: string;
}) {
  return (
    <input
      className="input mono"
      type="text"
      inputMode="decimal"
      aria-label={label}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      style={{ minWidth: 88 }}
    />
  );
}

/**
 * sampleBases picks values worth previewing: every band's lower edge, one
 * value inside each band, and one below the lowest band.
 *
 * The edges are the point. A step scale's bugs live exactly there — an
 * operator who means "10,000 to 100,000" and gets ₹10,000 charged at the
 * lower tier will only find out from a bill, unless the editor shows both
 * sides of the boundary while they're still typing.
 */
function sampleBases(
  rules: FeeRuleInput[],
  unit: "money" | "count",
): { basis: number; charge: number | null }[] {
  if (rules.length === 0) return [];
  const kind: FeeKind = unit === "count" ? "beneficiary" : "transaction";
  const edges = new Set<number>();
  for (const r of rules) {
    edges.add(r.basis_from_minor);
    // Just inside the band, so the preview shows the band's own rate rather
    // than only its boundary behaviour.
    const step = unit === "count" ? 1 : 100;
    edges.add(r.basis_from_minor + step);
    if (r.basis_to_minor != null) edges.add(r.basis_to_minor);
  }
  const lowest = Math.min(...rules.map((r) => r.basis_from_minor));
  if (lowest > 0) edges.add(Math.max(0, lowest - (unit === "count" ? 1 : 100)));

  return [...edges]
    .filter((b) => b >= 0)
    .sort((a, b) => a - b)
    .slice(0, 8)
    .map((basis) => ({ basis, charge: previewCharge(rules, kind, basis) }));
}
